import React, { useState } from "react";
import { useLocation } from "react-router";
import {
  AppLayout,
  BrandMark,
  GlobalNavRail,
  astryx,
  LayoutDashboard,
  MessagesSquare,
  ScrollText,
  UsersRound,
  Contact,
  Download,
  type GlobalNavRailItem,
} from "@shadowcue/ui";
import SettingsModal from "./settings_modal";
import type { IUser } from "../../types/iuser";

interface SidebarLayoutProps {
  currentUser: IUser;
  children: React.ReactNode;
}

const primaryNavItems: GlobalNavRailItem[] = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { label: "Conversations", href: "/dashboard/conversations", icon: MessagesSquare },
];

// Utility links pinned above Settings, like an account menu's "Get the app".
const footerNavItems: GlobalNavRailItem[] = [
  { label: "Desktop app", href: "/dashboard/downloads", icon: Download },
];

const adminNavItems: GlobalNavRailItem[] = [
  { label: "Script Templates", href: "/admin/script_templates", icon: ScrollText },
  { label: "Users", href: "/admin/users", icon: UsersRound },
  { label: "Contacts", href: "/admin/contacts", icon: Contact },
];

const csrfToken = (): string => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? (meta.getAttribute("content") ?? "") : "";
};

/**
 * Builds and submits the hidden form ContxtIQ signs out with. Kept as a
 * verbatim POST + `_method=delete` + CSRF token — Devise's
 * `config.sign_out_via = :delete` (config/initializers/devise.rb:269)
 * requires exactly this shape. Only the confirmation UI (an AlertDialog
 * replacing the old click-twice button) changed.
 */
const submitLogout = () => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/users/sign_out";

  const methodInput = document.createElement("input");
  methodInput.type = "hidden";
  methodInput.name = "_method";
  methodInput.value = "delete";
  form.appendChild(methodInput);

  const tokenInput = document.createElement("input");
  tokenInput.type = "hidden";
  tokenInput.name = "authenticity_token";
  tokenInput.value = csrfToken();
  form.appendChild(tokenInput);

  document.body.appendChild(form);
  form.submit();
};

const SidebarLayout = ({ currentUser, children }: SidebarLayoutProps) => {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = () => {
    setSigningOut(true);
    submitLogout();
  };

  return (
    <AppLayout
      sideNav={
        <GlobalNavRail
          items={primaryNavItems}
          adminItems={adminNavItems}
          isAdmin={currentUser.admin}
          currentPath={location.pathname}
          footerItems={footerNavItems}
          headingHref="/dashboard"
          headingIcon={<BrandMark />}
          onOpenSettings={() => setSettingsOpen(true)}
          onSignOut={() => setSignOutOpen(true)}
        />
      }
    >
      {children}

      <SettingsModal
        isOpen={settingsOpen}
        currentUser={currentUser}
        onClose={() => setSettingsOpen(false)}
      />

      <astryx.AlertDialog
        isOpen={signOutOpen}
        onOpenChange={setSignOutOpen}
        title="Sign out?"
        description="You'll need to sign in again to access ContxtIQ."
        actionLabel="Sign out"
        actionVariant="destructive"
        isActionLoading={signingOut}
        onAction={handleSignOut}
      />
    </AppLayout>
  );
};

export default SidebarLayout;
